/**
 * Web stand-in for `react-native-blob-util`.
 *
 * The app uses this module for exactly two things:
 *   1. Downloading a file to the device  (zen-core-elements `downloadFile`)
 *   2. PUT-ing a picked image to a signed S3 URL, streamed from a local path
 *      (src/shared/commonComponents/commonImageUpload.tsx)
 *
 * Browsers have no writable filesystem, so "download" means "hand the blob to
 * the browser's download manager", and a local path is a blob:/data: URL that
 * `fetch` can read directly.
 */

const WRAP_PREFIX = 'RNFB-WRAP::';

type FetchConfig = {
  fileCache?: boolean;
  path?: string;
  appendExt?: string;
  addAndroidDownloads?: {
    notification?: boolean;
    useDownloadManager?: boolean;
    path?: string;
    description?: string;
    mime?: string;
    title?: string;
  };
};

export type FetchResult = {
  data: string;
  respInfo: {status: number; headers: Record<string, string>};
  info: () => {status: number; headers: Record<string, string>};
  path: () => string;
  text: () => string;
  blob: () => Promise<Blob>;
  json: () => any;
  base64: () => string;
};

function filenameFrom(config: FetchConfig | undefined, url: string): string {
  const configured = (config && (config.path || (config.addAndroidDownloads || {}).path)) || '';
  const fromConfig = configured.split('/').pop();
  if (fromConfig) {
    return fromConfig;
  }
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.pathname.split('/').pop() || 'download';
  } catch {
    return 'download';
  }
}

function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next tick so the download has definitely started.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return window.btoa(binary);
}

async function resolveBody(body: any): Promise<BodyInit | undefined> {
  if (body == null) {
    return undefined;
  }
  if (typeof body === 'string' && body.startsWith(WRAP_PREFIX)) {
    // `RNFetchBlob.wrap(path)` - on web the "path" is a blob:/data: URL.
    const source = body.slice(WRAP_PREFIX.length);
    const response = await fetch(source);
    return await response.blob();
  }
  return body as BodyInit;
}

async function doFetch(
  config: FetchConfig | undefined,
  method: string,
  url: string,
  headers?: Record<string, string>,
  body?: any,
): Promise<FetchResult> {
  const response = await fetch(url, {
    method,
    headers,
    body: await resolveBody(body),
  });

  const blob = await response.blob();
  const shouldSave = Boolean(config && (config.fileCache || config.addAndroidDownloads));

  if (shouldSave && response.ok) {
    saveBlob(blob, filenameFrom(config, url));
  }

  const responseHeaders: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });

  const info = {status: response.status, headers: responseHeaders};
  let cachedText: string | null = null;

  return {
    // Callers only check truthiness of `data` to detect a successful download.
    data: shouldSave ? filenameFrom(config, url) : await blob.text(),
    respInfo: info,
    info: () => info,
    path: () => filenameFrom(config, url),
    text: () => {
      if (cachedText == null) {
        cachedText = '';
      }
      return cachedText;
    },
    blob: async () => blob,
    json: () => {
      throw new Error('json() is not supported by the web ReactNativeBlobUtil shim.');
    },
    base64: () => {
      throw new Error('base64() is synchronous and is not supported on web; use fs.readFile.');
    },
  };
}

const fs = {
  dirs: {
    DocumentDir: '',
    CacheDir: '',
    DownloadDir: '',
    DCIMDir: '',
    LegacyDCIMDir: '',
    LegacyDownloadDir: '',
    MainBundleDir: '',
    PictureDir: '',
    SDCardDir: '',
  },

  /** On web a "uri" is a blob:/data:/http: URL that fetch can read. */
  async readFile(uri: string, encoding: string = 'utf8'): Promise<string> {
    const response = await fetch(uri);
    const blob = await response.blob();
    if (encoding === 'base64') {
      return blobToBase64(blob);
    }
    return blob.text();
  },

  /**
   * There is no persistent filesystem to collide with, so nothing ever
   * "already exists". This makes zen-core-elements' generateFileName() return
   * the plain filename, which is exactly what a browser download wants.
   */
  async exists(_path: string): Promise<boolean> {
    return false;
  },

  async unlink(_path: string): Promise<void> {},
  async mkdir(_path: string): Promise<void> {},
  async writeFile(_path: string, _data: string, _encoding?: string): Promise<void> {
    throw new Error('fs.writeFile is not available in the browser.');
  },
};

const ReactNativeBlobUtil = {
  fs,
  wrap: (path: string) => `${WRAP_PREFIX}${path}`,
  fetch: (method: string, url: string, headers?: Record<string, string>, body?: any) =>
    doFetch(undefined, method, url, headers, body),
  config: (config: FetchConfig) => ({
    fetch: (method: string, url: string, headers?: Record<string, string>, body?: any) =>
      doFetch(config, method, url, headers, body),
  }),
};

export default ReactNativeBlobUtil;
